// src/components/documents/Documents.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';
import { Card, Table, Badge, Button, Modal, FormField, Input, Select, SearchInput, ResponsiveGrid, ConfirmDialog } from '../layout/UI';
import { useResponsive } from '../../hooks/useResponsive';
import LocationAutocomplete from './LocationAutocomplete';
import SavedLocationPicker from './SavedLocationPicker';
import RoutePreviewMap from './RoutePreviewMap';
import { getRoute, formatDuration, computeETA, formatETATime } from '../../services/geocoding';
import { reverseGeocode } from '../../services/traccar';

const DOC_STATUSES = {
  pending: { color: '#f59e0b', label: 'Pending' },
  'in-transit': { color: '#3b82f6', label: 'In Transit' },
  delivered: { color: '#10b981', label: 'Delivered' },
  cancelled: { color: '#ef4444', label: 'Cancelled' },
};

// Document types are admin-configurable in Settings — this is just the
// shape of a blank form; the actual `type` gets filled in once we know
// the current list (see openAdd below).
const emptyForm = (defaultType = '') => ({
  truckId: '', type: defaultType, docNumber: '',
  date: new Date().toISOString().split('T')[0],
  cargo: '', weight: '',
  origin: '', originLat: null, originLng: null,
  destination: '', destLat: null, destLng: null,
  distanceKm: null, etaMinutes: null,
});

export default function Documents() {
  const { documents, addDocument, updateDocument, deleteDocument, trucks, users, settings } = useApp();
  const documentTypes = settings.documentTypes || [];
  const { isMobile } = useResponsive();
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editDoc, setEditDoc] = useState(null); // the document being edited, or null when adding
  const [viewDoc, setViewDoc] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewRouteGeometry, setViewRouteGeometry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [routeGeometry, setRouteGeometry] = useState(null);
  const [routeLoading, setRouteLoading] = useState(false);

  // Fetch the route line for display when opening a document that has saved coordinates
  useEffect(() => {
    let cancelled = false;
    if (viewDoc?.originLat && viewDoc?.destLat) {
      getRoute(
        { lat: viewDoc.originLat, lng: viewDoc.originLng },
        { lat: viewDoc.destLat, lng: viewDoc.destLng }
      ).then(route => {
        if (!cancelled && route) setViewRouteGeometry(route.geometry);
      });
    } else {
      setViewRouteGeometry(null);
    }
    return () => { cancelled = true; };
  }, [viewDoc]);

  const filtered = documents.filter(d =>
    d.docNumber?.toLowerCase().includes(search.toLowerCase()) ||
    d.type?.toLowerCase().includes(search.toLowerCase()) ||
    d.origin?.toLowerCase().includes(search.toLowerCase()) ||
    d.destination?.toLowerCase().includes(search.toLowerCase())
  );

  // Auto-derives a short prefix for the doc number from the type name —
  // known types keep their familiar abbreviation, and any admin-added
  // custom type gets one generated from its initials (e.g. "Customs
  // Clearance" -> "CC") so this keeps working for new types with no
  // extra configuration needed.
  const genDocNumber = (type) => {
    const knownPrefixes = { 'Delivery Receipt': 'DR', 'Waybill': 'WB', 'Bill of Lading': 'BL' };
    const initials = (type || '').split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 4);
    const prefix = knownPrefixes[type] || initials || 'DOC';
    return `${prefix}-${new Date().getFullYear()}-${String(documents.length + 1).padStart(4, '0')}`;
  };

  const openAdd = () => {
    const type = documentTypes[0] || '';
    setEditDoc(null);
    setForm({ ...emptyForm(type), docNumber: genDocNumber(type) });
    setRouteGeometry(null);
    setModalOpen(true);
  };

  const openEdit = (doc) => {
    setEditDoc(doc);
    setForm({
      truckId: doc.truckId || '', type: doc.type || documentTypes[0] || '', docNumber: doc.docNumber || '',
      date: doc.date || new Date().toISOString().split('T')[0],
      cargo: doc.cargo || '', weight: doc.weight || '',
      origin: doc.origin || '', originLat: doc.originLat ?? null, originLng: doc.originLng ?? null,
      destination: doc.destination || '', destLat: doc.destLat ?? null, destLng: doc.destLng ?? null,
      distanceKm: doc.distanceKm ?? null, etaMinutes: doc.etaMinutes ?? null,
    });
    // Re-fetch the route line so the preview map shows the existing path immediately
    if (doc.originLat && doc.destLat) {
      setRouteLoading(true);
      getRoute({ lat: doc.originLat, lng: doc.originLng }, { lat: doc.destLat, lng: doc.destLng })
        .then(route => { setRouteGeometry(route?.geometry || null); setRouteLoading(false); });
    } else {
      setRouteGeometry(null);
    }
    setModalOpen(true);
  };

  const handleTypeChange = (type) => {
    setForm(f => ({ ...f, type, docNumber: editDoc ? f.docNumber : genDocNumber(type) }));
  };

  // Auto-compute route + ETA whenever both origin and destination have coordinates
  const computeRoute = useCallback(async (origin, destination) => {
    if (!origin?.lat || !destination?.lat) {
      setRouteGeometry(null);
      setForm(f => ({ ...f, distanceKm: null, etaMinutes: null }));
      return;
    }
    setRouteLoading(true);
    const route = await getRoute(origin, destination);
    setRouteLoading(false);
    if (route) {
      setRouteGeometry(route.geometry);
      setForm(f => ({ ...f, distanceKm: route.distanceKm, etaMinutes: route.durationMinutes }));
    } else {
      setRouteGeometry(null);
      setForm(f => ({ ...f, distanceKm: null, etaMinutes: null }));
    }
  }, []);

  const handleOriginSelect = (loc) => {
    setForm(f => ({ ...f, origin: loc.address, originLat: loc.lat, originLng: loc.lng }));
    computeRoute({ lat: loc.lat, lng: loc.lng }, { lat: form.destLat, lng: form.destLng });
  };

  const handleDestinationSelect = (loc) => {
    setForm(f => ({ ...f, destination: loc.address, destLat: loc.lat, destLng: loc.lng }));
    computeRoute({ lat: form.originLat, lng: form.originLng }, { lat: loc.lat, lng: loc.lng });
  };

  // Click (or drag) directly on the map to place a pin exactly, for
  // addresses that search can't find — very common with Philippine
  // subdivision streets that aren't individually mapped yet. Reverse-
  // geocodes the clicked point just for a readable label; the pin itself
  // is placed at the exact coordinates clicked, not wherever that label's
  // "official" location might be.
  const handleOriginMapClick = useCallback(async (pos) => {
    setForm(f => ({ ...f, originLat: pos.lat, originLng: pos.lng, origin: 'Locating...' }));
    const address = await reverseGeocode(pos.lat, pos.lng);
    setForm(f => (f.originLat === pos.lat && f.originLng === pos.lng ? { ...f, origin: address } : f));
    computeRoute(pos, { lat: form.destLat, lng: form.destLng });
  }, [form.destLat, form.destLng, computeRoute]);

  const handleDestinationMapClick = useCallback(async (pos) => {
    setForm(f => ({ ...f, destLat: pos.lat, destLng: pos.lng, destination: 'Locating...' }));
    const address = await reverseGeocode(pos.lat, pos.lng);
    setForm(f => (f.destLat === pos.lat && f.destLng === pos.lng ? { ...f, destination: address } : f));
    computeRoute({ lat: form.originLat, lng: form.originLng }, pos);
  }, [form.originLat, form.originLng, computeRoute]);

  const handleSave = () => {
    if (!form.truckId || !form.docNumber) return;
    const etaTimestamp = form.etaMinutes != null ? computeETA(form.etaMinutes) : null;
    if (editDoc) {
      updateDocument(editDoc.id, { ...form, etaTimestamp });
    } else {
      addDocument({ ...form, etaTimestamp });
    }
    setModalOpen(false);
  };

  const columns = [
    {
      key: 'docNumber', label: 'Document',
      render: (val, row) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--textPrimary)', fontFamily: 'Syne, sans-serif' }}>{val}</div>
          <div style={{ fontSize: 11, color: 'var(--textFaint)' }}>{row.type}</div>
        </div>
      )
    },
    {
      key: 'truckId', label: 'Truck',
      render: v => {
        const truck = trucks.find(t => t.id === v);
        return truck ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: truck.color }} />
            <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{truck.plateNumber}</span>
          </div>
        ) : <span style={{ color: 'var(--textFaint)' }}>—</span>;
      }
    },
    { key: 'origin', label: 'Origin', render: v => <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{v || '—'}</span> },
    { key: 'destination', label: 'Destination', render: v => <span style={{ fontSize: 12, color: 'var(--textSecondary)' }}>{v || '—'}</span> },
    {
      key: 'etaMinutes', label: 'ETA',
      render: (v, row) => {
        if (row.status === 'delivered') return <Badge color="#10b981">Delivered</Badge>;
        if (row.status === 'cancelled') return <span style={{ color: 'var(--textFaint)' }}>—</span>;
        if (v == null) return <span style={{ color: 'var(--textFaint)', fontSize: 11 }}>—</span>;
        return (
          <div>
            <div style={{ fontSize: 12, color: '#3b82f6', fontWeight: 600 }}>{formatDuration(v)}</div>
            {row.distanceKm != null && <div style={{ fontSize: 10, color: 'var(--textFaint)' }}>{row.distanceKm} km</div>}
          </div>
        );
      }
    },
    { key: 'date', label: 'Date', render: v => <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>{v}</span> },
    {
      key: 'status', label: 'Status',
      render: (v, row) => (
        <Select
          value={v}
          onChange={newStatus => { updateDocument(row.id, { status: newStatus }); }}
          options={Object.entries(DOC_STATUSES).map(([k, s]) => ({ value: k, label: s.label }))}
        />
      )
    },
    {
      key: 'encodedBy', label: 'Encoded By',
      render: v => {
        const user = users.find(u => u.id === v);
        return <span style={{ fontSize: 11, color: 'var(--textFaint)' }}>{user?.name || '—'}</span>;
      }
    },
    {
      key: 'id', label: '',
      render: (id, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setViewDoc(row); }}>View</Button>
          <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
          <Button size="sm" variant="danger" onClick={e => { e.stopPropagation(); setConfirmDelete(row); }}>Del</Button>
        </div>
      )
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center', marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search documents..." />
          </div>
          <Button onClick={openAdd} icon="＋">Encode Document</Button>
        </div>

        <ResponsiveGrid columns={4} tabletColumns={2} mobileColumns={2} gap={10} style={{ marginBottom: 20 }}>
          {Object.entries(DOC_STATUSES).map(([key, status]) => {
            const count = documents.filter(d => d.status === key).length;
            return (
              <div key={key} style={{ background: 'var(--bgInput)', border: `1px solid ${status.color}22`, borderRadius: 10, padding: '12px 16px' }}>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 26, color: status.color }}>{count}</div>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--textMuted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 2 }}>{status.label}</div>
              </div>
            );
          })}
        </ResponsiveGrid>

        <Table columns={columns} data={filtered} emptyMessage="No documents found" />
      </Card>

      {/* Encode / Edit Document Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editDoc ? `Edit Document — ${editDoc.docNumber}` : 'Encode New Document'} width={640}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
          <FormField label="Document Type">
            <Select value={form.type} onChange={handleTypeChange} options={documentTypes.map(t => ({ value: t, label: t }))} />
          </FormField>
          <FormField label="Document Number" required>
            <Input value={form.docNumber} onChange={v => setForm(f => ({ ...f, docNumber: v }))} placeholder="Auto-generated" />
          </FormField>
          <FormField label="Assigned Truck" required>
            <Select value={form.truckId} onChange={v => setForm(f => ({ ...f, truckId: v }))} placeholder="Select truck" options={trucks.map(t => ({ value: t.id, label: `${t.plateNumber} - ${t.model}` }))} />
          </FormField>
          <FormField label="Date">
            <Input value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} type="date" />
          </FormField>
        </div>

        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
          <FormField label="Origin" hint="Start typing a location — or just click the map below to pin the exact spot">
            <LocationAutocomplete
              value={form.originLat ? { address: form.origin, lat: form.originLat } : { address: form.origin }}
              onSelect={handleOriginSelect}
              placeholder="Loading point"
              icon="🟢"
            />
            <SavedLocationPicker
              currentValue={form.originLat ? { address: form.origin, lat: form.originLat, lng: form.originLng } : null}
              onPick={(loc) => handleOriginSelect({ address: loc.name, lat: loc.lat, lng: loc.lng })}
            />
          </FormField>
          <FormField label="Destination" hint="Start typing a location — or just click the map below to pin the exact spot">
            <LocationAutocomplete
              value={form.destLat ? { address: form.destination, lat: form.destLat } : { address: form.destination }}
              onSelect={handleDestinationSelect}
              placeholder="Delivery point"
              icon="🟠"
            />
            <SavedLocationPicker
              currentValue={form.destLat ? { address: form.destination, lat: form.destLat, lng: form.destLng } : null}
              onPick={(loc) => handleDestinationSelect({ address: loc.name, lat: loc.lat, lng: loc.lng })}
            />
          </FormField>
        </div>

        {/* Route preview + ETA */}
        <div style={{ marginTop: 14 }}>
          <RoutePreviewMap
            origin={form.originLat ? { lat: form.originLat, lng: form.originLng } : null}
            destination={form.destLat ? { lat: form.destLat, lng: form.destLng } : null}
            routeGeometry={routeGeometry}
            height={280}
            editable
            onOriginClick={handleOriginMapClick}
            onDestinationClick={handleDestinationMapClick}
          />
          {(routeLoading || form.etaMinutes != null) && (
            <div style={{ display: 'flex', gap: 20, marginTop: 10, padding: '10px 14px', background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 9 }}>
              {routeLoading ? (
                <span style={{ fontSize: 12, color: 'var(--textMuted)' }}>⏳ Calculating route...</span>
              ) : (
                <>
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Distance</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--textPrimary)' }}>{form.distanceKm} km</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Est. Travel Time</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6' }}>{formatDuration(form.etaMinutes)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Est. Arrival</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#10b981' }}>{formatETATime(computeETA(form.etaMinutes))}</div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: 14, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
          <FormField label="Cargo Description">
            <Input value={form.cargo} onChange={v => setForm(f => ({ ...f, cargo: v }))} placeholder="e.g. Electronic Components" />
          </FormField>
          <FormField label="Weight / Volume">
            <Input value={form.weight} onChange={v => setForm(f => ({ ...f, weight: v }))} placeholder="e.g. 2.5 tons" />
          </FormField>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button onClick={handleSave}>{editDoc ? 'Save Changes' : 'Encode Document'}</Button>
        </div>
      </Modal>

      {/* View Doc Modal */}
      <Modal open={!!viewDoc} onClose={() => setViewDoc(null)} title="Document Details" width={560}>
        {viewDoc && (() => {
          const truck = trucks.find(t => t.id === viewDoc.truckId);
          const encoder = users.find(u => u.id === viewDoc.encodedBy);
          const s = DOC_STATUSES[viewDoc.status] || DOC_STATUSES.pending;
          const hasRoute = viewDoc.originLat && viewDoc.destLat;
          return (
            <div>
              <div style={{ padding: 16, background: 'var(--bgInput)', borderRadius: 12, border: '1px solid var(--borderInput)', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--textPrimary)' }}>{viewDoc.docNumber}</div>
                    <div style={{ fontSize: 12, color: 'var(--textMuted)', marginTop: 2 }}>{viewDoc.type}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setViewDoc(null); openEdit(viewDoc); }}>Edit</Button>
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Badge color={s.color}>{s.label}</Badge>
                  {viewDoc.distanceKm != null && <Badge color="#3b82f6">{viewDoc.distanceKm} km</Badge>}
                  {viewDoc.etaMinutes != null && viewDoc.status === 'in-transit' && (
                    <Badge color="#10b981">ETA {formatETATime(viewDoc.etaTimestamp)} ({formatDuration(viewDoc.etaMinutes)})</Badge>
                  )}
                </div>
              </div>

              {hasRoute && (
                <div style={{ marginBottom: 16 }}>
                  <RoutePreviewMap
                    origin={{ lat: viewDoc.originLat, lng: viewDoc.originLng }}
                    destination={{ lat: viewDoc.destLat, lng: viewDoc.destLng }}
                    routeGeometry={viewRouteGeometry}
                    height={180}
                  />
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Truck', value: truck ? `${truck.plateNumber} - ${truck.model}` : '—' },
                  { label: 'Date', value: viewDoc.date },
                  { label: 'Origin', value: viewDoc.origin },
                  { label: 'Destination', value: viewDoc.destination },
                  { label: 'Cargo', value: viewDoc.cargo },
                  { label: 'Weight', value: viewDoc.weight },
                  { label: 'Encoded By', value: encoder?.name || '—' },
                ].map(item => (
                  <div key={item.label} style={{ padding: '10px 14px', background: 'var(--bgInput)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 10, color: 'var(--textFaint)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 13, color: 'var(--textSecondary)' }}>{item.value || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </Modal>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => deleteDocument(confirmDelete.id)}
        title="Delete Document"
        message={`Delete "${confirmDelete?.docNumber}"? This cannot be undone.`}
      />
    </div>
  );
}
